import getConfig from 'next/config';
import { handleTrack } from '../../../../../../utils/tracker';
import { ButtonVariants } from '../../../../../Atoms/Button';
import { If } from '../../../../../Atoms/Conditionals';
import { ButtonWithAnchor } from '../../../../../income-tax/page-sections/hero-section/sticky-button';
import { PricingCardStaticDetailsTypes } from '../../../../../partner-pages/page-sections/eas-section/expertPlans/card-details.constants';
import { getDiscountValueText } from '../../../../../partner-pages/price_cards/utils';
import { IService } from '../../../../../partner-pages/types';
import useCopy from '../../../../../partner-pages/utils/hooks/use-copy';
import { sanitizeRichText } from '../../../../../partner-pages/utils/textUtils';
import { getIsITRPlan } from '../../../../../partner-pages/utils/utils';
import { addCommasToNumber } from '../../../../../Utils/CommonUtils';
import { TPricingPlans } from '../../../types';
import {
  ACCENTURE_EAS_DATA,
  ACCENTURE_SLUG,
} from '../../../../../partner-pages/constants';

interface PricingCardProps {
  card: PricingCardStaticDetailsTypes | undefined;
  pricingPlanData: TPricingPlans;
}
const { publicRuntimeConfig } = getConfig();

export const PricingCard = ({ card, pricingPlanData }: PricingCardProps) => {
  const slug = pricingPlanData.partnerSlug;
  const planId = pricingPlanData.externalId;
  const baseUrl = publicRuntimeConfig.CLEARTAX_DOMAIN;
  const planDetails = pricingPlanData.planDetails;
  const delay = 1000;
  const { copied, handleCopyCoupon } = useCopy(
    pricingPlanData?.autoApplicableCoupon?.code || '',
    delay
  );
  const BUY_NOW_LINK = `${baseUrl}/checkout/${planDetails.listingId}?coupon=${pricingPlanData?.autoApplicableCoupon?.code}`;
  const KNOW_MORE_LINK = `${baseUrl}/services/${pricingPlanData.externalId}/p`;
  const isItrPlan = getIsITRPlan(planDetails.productServiceType);
  const strikedOffPrice = planDetails.offerPricing.originalPrice;
  const discountedPrice = Math.floor(
    planDetails.pricing.totalAmount - planDetails.pricing.tax
  );
  const discountValue = getDiscountValueText(
    pricingPlanData?.autoApplicableCoupon,
    strikedOffPrice,
    discountedPrice,
    isItrPlan
  );
  const redirectionLink = `${baseUrl}/checkout/${planDetails.listingId}?coupon=${pricingPlanData?.autoApplicableCoupon?.code}&utm_ref=plan_partner_page_card`;
  return (
    <div
      className="sm:w-full border border-green-120 rounded-xxl"
      style={{ background: card?.bgColor }}
    >
      <a href={redirectionLink} className="text-black">
        <div className="top-section flex justify-between font-medium gap-6">
          <div className="pl-6 pt-6 pb-8 sm:pl-4 sm:pb-6">
            <div className="font-semibold text-s-24 sm:text-s-20">
              {planDetails.name.split(' - ')[1]}
            </div>
            <div className="flex items-baseline">
              <div className="font-bold text-s-32 mr-2 sm:text-s-28">
                ₹
                {addCommasToNumber(
                  slug === ACCENTURE_SLUG
                    ? ACCENTURE_EAS_DATA[
                        planId as keyof typeof ACCENTURE_EAS_DATA
                      ]?.discountedPrice
                    : Math.floor(planDetails.pricing.discountedPrice)
                )}
              </div>
              <div className="relative mr-1.5">
                <div className="border-b border-font-200 absolute w-full top-1/2"></div>
                <div className="text-font-200 text-s-14 mr-1">
                  ₹
                  {addCommasToNumber(
                    slug === ACCENTURE_SLUG
                      ? ACCENTURE_EAS_DATA[
                          planId as keyof typeof ACCENTURE_EAS_DATA
                        ]?.listPrice
                      : Math.floor(planDetails.pricing.listPrice)
                  )}
                </div>
              </div>
              <div className="text-s-14 text-purple-300">
                {slug === ACCENTURE_SLUG
                  ? ACCENTURE_EAS_DATA[
                      planId as keyof typeof ACCENTURE_EAS_DATA
                    ]?.discount
                  : discountValue}
              </div>
            </div>
            <div className="text-font-200 text-s-14">{}</div>
          </div>
          <If condition={!!card?.topSectionImg}>
            <div>
              {card?.topSectionImg ? (
                <img src={card.topSectionImg} alt="top-img" />
              ) : null}
            </div>
          </If>
        </div>
      </a>
      {pricingPlanData?.autoApplicableCoupon && (
        <div
          className="middle-top-section bg-white px-6 py-2 flex justify-between items-center sm:py-1.5 sm:px-4 cursor-pointer"
          onClick={() => {
            handleCopyCoupon();
            handleTrack({
              label: `plan-card_copy`,
              obj: null,
              value: pricingPlanData?.planDetails.name,
            });
          }}
        >
          <div className="text-s-20 font-bold sm:text-s-16">
            Extra{' '}
            {Math.floor(pricingPlanData.autoApplicableCoupon.discountValue)}%
            OFF
          </div>
          <div className="flex items-center">
            <span className="text-s-14 sm:text-s-12 text-font-200 mr-1">
              Code:{' '}
            </span>
            <span className="text-s-14 sm:text-s-12 text-blue-350 font-medium">
              {pricingPlanData.autoApplicableCoupon.code}
            </span>
            <img
              className="w-6 h-6 ml-1.5"
              src={
                copied
                  ? 'https://assets.clear.in/cleartax/images/1712001391_checkcircle1.svg'
                  : 'https://assets.clear.in/cleartax/images/1714203952_copy.svg'
              }
              alt="copy coupon"
            />
          </div>
        </div>
      )}
      <div>
        <a href={redirectionLink} className="text-black">
          <div className="px-5 pt-6">
            {planDetails.whenRequired.map((features: IService) => {
              const sanitizedRichText = sanitizeRichText(features.richText);

              return (
                <div
                  className="flex gap-1 items-center font-medium"
                  key={features.id}
                >
                  <img
                    className="w-6"
                    src="https://assets.clear.in/cleartax/images/1745998628_checkcircle.png"
                    alt="checkmark"
                  />
                  <div
                    className="text-font-500 text-s-16 font-medium"
                    dangerouslySetInnerHTML={{ __html: sanitizedRichText }}
                  ></div>
                </div>
              );
            })}
          </div>
          <div className="gap-4 flex px-5 my-7">
            {card && card.additionInfo && card.additionInfo.icon ? (
              <img
                src={card.additionInfo.icon}
                alt="phone-call"
                className="w-8 h-8"
              />
            ) : null}
            <div
              className={`font-semibold h-[70px] line-clamp-3`}
              style={{
                color: card?.additionInfo.fontColor,
              }}
            >
              {card?.additionInfo.text}
            </div>
          </div>
        </a>
        <div className="px-4">
          <ButtonWithAnchor
            trackLabel={`plan-card`}
            buttonText="Buy now"
            buttonVariant={ButtonVariants.bluefilled}
            className="text-base px-2 py-2"
            buttonLink={BUY_NOW_LINK}
            value={BUY_NOW_LINK}
            target="_blank"
          />
          <ButtonWithAnchor
            trackLabel={'plan-card'}
            buttonText="Know more"
            buttonVariant={ButtonVariants.bluetext}
            className="text-base w-full pt-4 pb-8 focus:outline-none active:outline-none"
            buttonLink={KNOW_MORE_LINK}
            value={BUY_NOW_LINK}
            target="__blank"
          />
        </div>
      </div>
    </div>
  );
};
